<script lang="ts" setup>
import { watch, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import './App.scss';
import storeAuth from '@store/auth';
import { User } from './types/user.type';
import SelectLanguages from '@components/SelectLanguages.vue';
import { useI18n } from 'vue-i18n';
import SideBar2 from '@components/SideBar.vue';
import ChubbnetStatus from '@components/ChubbnetStatus.vue';
import { resolveLogoUrl } from '@helpers/logo.helpers';

const { t, locale } = useI18n();
const { verifyPermission } = storeAuth()
const URLBACKEND = import.meta.env.VITE_APP_API;

const store = storeAuth();
const route = useRoute();
const router = useRouter();

const filterMenu = ref();

const templateLogo = computed(() => {
  if (!store.user?.template || store.user.template.deletedAt) {
    return '/img/tryger-logo.png';
  }
  const resolvedLogo = store.user.template.imageUrl
    ? store.user.template.imageUrl
    : resolveLogoUrl(
      typeof store.user.template.imagePath === 'string'
        ? store.user.template.imagePath
        : '',
      URLBACKEND,
    );
  return resolvedLogo || '/img/tryger-logo.png';
});

const getMenu = () => ({
  top: [
    {
      title: t('app.operaciones'),
      permission: 'Nueva Operacion',
      image: '/icons/operations.svg',
      children: [
        {
          title: t('app.nueva_operacion'),
          permission: 'Nueva Operacion',
          image: '/icons/new-operation.svg',
          visible: true,
          path: '/panel/nueva-operacion',
        },
        {
          title: t('app.operaciones_terrestres'),
          permission: 'Operaciones',
          image: '/icons/truck_container.svg',
          visible: true,
          path: '/panel/operaciones',
        },
        {
          title: t('app.operaciones_maritimas'),
          permission: 'Operaciones Maritimas',
          image: '/icons/ship_container.svg',
          visible: true,
          path: '/panel/operaciones-maritimas',
        },
        {
          title: t('app.operaciones_de_paqueteria'),
          permission: 'Operaciones de Paqueteria',
          image: '/icons/package2.svg',
          visible: true,
          path: '/panel/operaciones-paqueteria',
        },
      ],
    },

    {
      title: t('app.certificados'),
      permission: 'Emision de Certificados',
      image: '/icons/certificate.svg',
      children: [
        {
          title: t('app.emision_de_cerificados'),
          permission: 'Emision de Certificados',
          image: '/icons/create-issue-policie.svg',
          visible: true,
          path: '/panel/emision-de-certificados',
        },
        {
          title: t('app.listar_certificados'),
          permission: 'Listar Certificados',
          image: '/icons/list-issue-policies.svg',
          visible: true,
          path: '/panel/certificados',
        },
      ],
    },
    {
      title: t('app.polizas_api'),
      permission: 'Nueva Operacion',
      image: '/icons/lista.svg',
      children: [
        {
          title: t('app.listar_polizas_terrestres'),
          permission: 'Listar Polizas',
          image: '/icons/truck2.svg',
          visible: true,
          path: '/panel/polizas-terrestres',
        },
        {
          title: t('app.listar_polizas_maritimas'),
          permission: 'Listar Polizas Maritimas',
          image: '/icons/ship.svg',
          visible: true,
          path: '/panel/polizas-maritimas',
        },
        {
          title: t('app.listar_polizas_paqueteria'),
          permission: 'Listar Polizas de Paqueteria',
          image: '/icons/package.svg',
          visible: true,
          path: '/panel/polizas-paqueteria',
        },
        {
          title: t('app.listar_polizas_container'),
          permission: 'Listar Polizas Container',
          image: '/icons/container.svg',
          visible: true,
          path: '/panel/polizas-container',
        },
      ],
    },
    {
      title: t('app.ajustes'),
      permission: 'Ajustes',
      image: '/icons/ajustes.svg',
      visible: true,
      path: '/panel/ajustes/companias-gps',
    },
    {
      title: t('app.actualizar_poliza_chubb'),
      permission: 'Actualizar poliza Chubb',
      image: '/icons/update.svg',
      path: '/panel/numeracion-polizas',
      visible: true,
    },
    {
      title: t('app.seguimiento_web'),
      permission: 'Seguimiento web',
      image: '/icons/tracking.svg',
      path: '/panel/seguimiento-web',
      visible: true,
    },
    {
      title: t('app.log_emails'),
      permission: 'Logs Emails',
      image: '/icons/email-search.svg',
      path: '/panel/log-emails',
      visible: true,
    },
    {
      title: t('app.log_polizas_api'),
      permission: 'Logs Polizas API',
      image: '/icons/document-search.svg',
      path: '/panel/log-policies-api',
      visible: true,
    },
  ],
  above: [
    {
      title: 'NOC logs',
      image: '/icons/error.svg',
      visible: true,
      name: { name: 'Noc' },
    },
    {
      title: t('app.cerrar_sesion'),
      image: '/icons/logout.svg',
      visible: true,
      name: { name: 'Logout' },
    },
  ],
});

const regenerateMenu = () => {
  const user = store.user as User;
  const menu = getMenu();

  if (!user?.permission) return;

  const filteredTopMenu = menu.top
    .map((item) => {
      if (item.children && Array.isArray(item.children)) {
        // Filtrar los children con permisos
        const filteredChildren = item.children.filter((child) =>
          user.permission.includes(child.permission),
        );

        if (filteredChildren.length > 0) {
          // Devolver el ítem padre con los hijos filtrados
          return {
            ...item,
            children: filteredChildren,
          };
        }

        // No tiene hijos válidos, se descarta
        return null;
      } else {
        // Es un ítem sin hijos, validar permiso directo
        if (user.permission.includes(item.permission)) {
          return item;
        }
        return null;
      }
    })
    .filter(Boolean); // Elimina los null

  const filterAboveMenu = menu.above.filter((item) => {
    if (!user.isDev) {
      return item.title !== 'NOC logs';
    }
    return true;
  });

  filterMenu.value = {
    top: filteredTopMenu,
    above: filterAboveMenu,
  };
};

// Cuando cambia el user o el idioma
watch([() => store.user, locale], regenerateMenu, { immediate: true });

watch(
  () => store.user,
  (user) => {
    if (user) {
      Sentry.setUser({ id: String(user.id), email: user.email });
    } else {
      Sentry.setUser(null);
    }
  },
  { immediate: true },
);

const hideLayouts = [
  'Login',
  'Logout',
  'Home',
  'Contratacion',
  'Recover password',
  'Send mail recovery',
];

const handleNavigation = (data: any) => {
  if (data.path) {
    return router.push({ path: data.path });
  }
  if (data.name) {
    router.push(data.name);
  }
};
</script>

<template>
  <div class="AppRoot-fullPage">
    <NavBar
      v-if="!hideLayouts.includes(route.name as string) && store.user"
      :logo="templateLogo"
      title=""
      :className="'NavBar__tryger'"
      :background="
        store.user.template && !store.user.template.deletedAt
          ? store.user.template.primaryColour
          : '#505050'
      "
    />
    <SelectLanguages v-if="store.user?.isDev" />
    <ChubbnetStatus v-if="store.user?.roles?.includes('admin') && verifyPermission('Mostrar Estado Chubbnet')" />

    <div class="AppMain">
      <div class="AppMain__layer">
        <SideBar2
          v-if="!hideLayouts.includes(route.name as string) && store.user"
          :menu="filterMenu"
          @navigate="handleNavigation"
        />
        <!-- <SideBar DEPRECATED
          v-if="!hideLayouts.includes(route.name as string) && store.user"
          :menu="filterMenu"
          @navigate="handleNavigation"
          :background="
            store.user.template && !store.user.template.deletedAt
              ? store.user.template.primaryColour
              : '#505050'
          "
        /> -->
        <main class="AppMain__layer__PageAndTopBar">
          <div class="HomePageContent">
            <div class="Scrollable--vertical HomePageContent__contentContainer">
              <router-view></router-view>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<style>
.Sidebar__Top {
  max-height: 90% !important;
}
</style>
